import { useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { currentLanguage, switchLanguage } from "../services/locale";
import { useCurrency } from "../services/currency";

export const LANGUAGES = [
    { code: "en", label: "English" },
    { code: "ar", label: "العربية" },
];

// "UAE Dirham" in the current site language, falling back to the API's English name.
export function currencyName(code, fallback, lang) {
    try {
        return new Intl.DisplayNames([lang], { type: "currency" }).of(code) || fallback;
    } catch (e) {
        return fallback;
    }
}

// The two selects, shared by the desktop popover and the mobile drawer.
export function LocaleFields({ idPrefix }) {
    const { t } = useTranslation();
    const { currency, currencies, setCurrency } = useCurrency();
    const lang = currentLanguage();

    return (
        <>
            <label className="ls-label" htmlFor={`${idPrefix}-lang`}>{t("header.language")}</label>
            <select id={`${idPrefix}-lang`} className="form-select ls-select" value={lang} onChange={(e) => switchLanguage(e.target.value)}>
                {LANGUAGES.map((l) => <option key={l.code} value={l.code}>{l.label}</option>)}
            </select>

            <label className="ls-label" htmlFor={`${idPrefix}-currency`}>{t("header.currency")}</label>
            <select id={`${idPrefix}-currency`} className="form-select ls-select" value={currency} onChange={(e) => setCurrency(e.target.value)}>
                {currencies.map((c) => (
                    <option key={c.code} value={c.code}>{c.code} — {currencyName(c.code, c.name, lang)}</option>
                ))}
            </select>

            {currency !== "USD" && <p className="ls-note">{t("header.currency_note")}</p>}
        </>
    );
}

// Desktop header: "English, USD ▾" opening the language + currency panel.
export default function LocaleSwitcher() {
    const { currency } = useCurrency();
    const [open, setOpen] = useState(false);
    const rootRef = useRef(null);
    const panelId = useId();
    const lang = LANGUAGES.find((l) => l.code === currentLanguage()) || LANGUAGES[0];

    useEffect(() => {
        if (!open) return undefined;
        const onDown = (e) => rootRef.current && !rootRef.current.contains(e.target) && setOpen(false);
        const onKey = (e) => e.key === "Escape" && setOpen(false);
        document.addEventListener("mousedown", onDown);
        document.addEventListener("keydown", onKey);
        return () => {
            document.removeEventListener("mousedown", onDown);
            document.removeEventListener("keydown", onKey);
        };
    }, [open]);

    return (
        <div className="ls-root" ref={rootRef}>
            <button
                type="button"
                className="ls-toggle"
                aria-expanded={open}
                aria-controls={panelId}
                onClick={() => setOpen(!open)}
            >
                <span>{lang.label}, {currency}</span>
                <i className={`fa-light fa-chevron-down ls-chevron ${open ? "is-open" : ""}`} />
            </button>
            {open &&
                <div className="ls-panel" id={panelId} role="dialog">
                    <LocaleFields idPrefix="ls-desktop" />
                </div>
            }
        </div>
    );
}
